import { useEffect, useRef, useState } from "react";

import { Loader, Select } from "@/components/ui/elements";

import { isErrorResponse, useGetBanks } from "@/hooks/bank";

type Props = {
  defaultbank: string | undefined;
  defaultbankName: string | undefined;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  getBankCode: (code: string) => void;
  getBankName: (name: string) => void;
};

type BankItem = {
  value: string;
  label: string;
};

export function BankNameInput({
  defaultbank,
  defaultbankName,
  searchValue: externalSearchValue,
  onSearchChange: externalOnSearchChange,
  getBankCode,
  getBankName,
}: Props) {
  const [bankList, setBankList] = useState<BankItem[]>([]);
  const [selectedValue, setSelectedValue] = useState<string | null>(
    defaultbank || null,
  );
  const mountedRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  const cooldownRef = useRef(false);
  const cancel = () => window.clearTimeout(timeoutRef.current!);

  // 外部制御がある場合はそれを使用、ない場合は内部状態を使用
  const [internalSearchValue, setInternalSearchValue] = useState(
    defaultbankName || "",
  );

  const searchValue =
    externalSearchValue !== undefined
      ? externalSearchValue
      : internalSearchValue;
  const onSearchChange = externalOnSearchChange || setInternalSearchValue;

  const { getFilterBank, loading } = useGetBanks();

  function filterBank(query: string) {
    // 空文字列の場合はリストをクリア
    if (query === "") {
      setBankList([]);
      return;
    }

    getFilterBank(query)
      .then((result) => {
        if (!result) return setBankList([]);
        if (isErrorResponse(result)) return setBankList([]);
        if (!result.length) return setBankList([]);
        const list = result.map((item) => {
          return { value: item.code, label: item.name };
        });
        setBankList(list);
      })
      .catch(() => {
        return setBankList([]);
      });
  }

  useEffect(() => {
    mountedRef.current = true;
    return cancel;
  }, []);

  useEffect(() => {
    if (mountedRef.current) {
      if (!cooldownRef.current) {
        cooldownRef.current = true;
        filterBank(searchValue);
      } else {
        cancel();
        timeoutRef.current = window.setTimeout(() => {
          cooldownRef.current = false;
          filterBank(searchValue);
        }, 200);
      }
    }
  }, [searchValue]);

  // defaultbankの変更を監視（編集時の状態同期）
  useEffect(() => {
    // defaultbankが空になった場合は選択値もクリア
    if (!defaultbank && selectedValue) {
      setSelectedValue(null);
    }
    // defaultbankが設定され、現在の選択値と異なる場合は同期
    else if (defaultbank && selectedValue !== defaultbank) {
      setSelectedValue(defaultbank);
    }
  }, [defaultbank]);

  // 編集時の初期化（一度だけ実行）
  useEffect(() => {
    if (defaultbankName && externalOnSearchChange && !externalSearchValue) {
      externalOnSearchChange(defaultbankName);
    }
  }, []); // 空の依存配列で初回のみ実行

  const handleSearchChange = (value: string) => {
    onSearchChange(value);
  };

  const handleChange = (value: string | null) => {
    setSelectedValue(value);
    if (value) {
      getBankCode(value);
    } else {
      // 値が削除された場合は空文字列で親に通知
      getBankCode("");
      getBankName("");
      // 検索値もクリア
      onSearchChange("");
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    getBankName(e.target.value);
  };

  return (
    <Select
      value={selectedValue}
      withAsterisk
      data={bankList}
      searchable
      clearable={!loading} // ローディング中はクリアボタンを無効化
      onSearchChange={handleSearchChange}
      searchValue={searchValue}
      placeholder=""
      description="金融機関名を入力し、入力後に表示される候補の中から選択してください。"
      maw={200}
      label="金融機関名"
      nothingFoundMessage={loading ? <Loader /> : "金融機関が見つかりません。"}
      limit={20}
      onChange={handleChange}
      onBlur={handleBlur}
      rightSection={loading ? <Loader size="xs" /> : undefined}
    />
  );
}
